'use client'

import { ArrowUpward, AttachFile, Stop } from '@mui/icons-material'
import { Box, IconButton, Stack, TextField, Tooltip } from '@mui/material'
import { useTranslations } from 'next-intl'
import { type ChangeEvent, type KeyboardEvent, useRef, useState } from 'react'

import type { FocusedContextRef } from '~/features/assistant/contextRef'
import type { AssistantMentionCandidate } from '~/features/assistant/useAssistantMentions'

import ActModeToggle from './ActModeToggle'
import ContextRefChips from './ContextRefChips'
import MentionPicker, { type MentionPickerHandle } from './MentionPicker'
import AttachmentChip from './attachments/AttachmentChip'
import { ACCEPTED_ATTACHMENT_TYPES, type PendingAttachment } from './attachments/useAttachmentUploads'
import { contextRefKey, withContextRef } from './contextRefs'

const FAILURE_MESSAGES = {
  tooLarge: 'attachmentTooLarge',
  tooMany: 'attachmentTooMany',
  uploadFailed: 'attachmentFailed'
} as const

type ComposerProps = {
  onSend: (text: string) => void
  /** Present while a reply streams; the send button becomes a stop button. */
  onStop?: () => void
  disabled?: boolean
  /** Entities tagged with `@` for the next message. */
  contextRefs: FocusedContextRef[]
  onContextRefsChange: (contextRefs: FocusedContextRef[]) => void
  /** Files attached to the next message; with `onAttach`, the composer offers to attach more. */
  attachments?: PendingAttachment[]
  onAttach?: (files: File[]) => void
  onRemoveAttachment?: (key: string) => void
}

/**
 * The message box. Enter sends, Shift+Enter starts a new line, and an `@` opens a picker of entities to tag the
 * message with. A message needs text or an attached file, and waits for every attachment to finish uploading. Below
 * it sits the Ask / Act toggle.
 */
export default function Composer({
  onSend,
  onStop,
  disabled = false,
  contextRefs,
  onContextRefsChange,
  attachments = [],
  onAttach,
  onRemoveAttachment
}: ComposerProps) {
  const t = useTranslations('features.AssistantV2')
  const [draft, setDraft] = useState('')
  const [dismissedMentionAt, setDismissedMentionAt] = useState<number>()
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const pickerRef = useRef<MentionPickerHandle>(null)
  const fileInput = useRef<HTMLInputElement>(null)
  const text = draft.trim()
  const isUploading = attachments.some((item) => item.status === 'uploading')
  const hasFiles = attachments.some((item) => item.status === 'ready')
  const canSend = !disabled && !onStop && !isUploading && (text.length > 0 || hasFiles)
  const mention = findMention(draft)
  const isPicking = !disabled && !!mention && mention.index !== dismissedMentionAt

  // A dismissed `@` stays dismissed only while it is still the one being typed, so the next `@` opens the picker.
  const changeDraft = (next: string) => {
    setDraft(next)
    if (findMention(next)?.index !== dismissedMentionAt) setDismissedMentionAt(undefined)
  }

  const send = () => {
    if (!canSend) return
    onSend(text)
    changeDraft('')
  }

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (pickerRef.current?.handleKeyDown(event)) return
    if (event.key !== 'Enter' || event.shiftKey || event.nativeEvent.isComposing) return
    event.preventDefault()
    send()
  }

  const pick = (candidate: AssistantMentionCandidate) => {
    onContextRefsChange(withContextRef(contextRefs, candidate.contextRef))
    if (mention) changeDraft(draft.slice(0, mention.index))
    inputRef.current?.focus()
  }

  const removeContextRef = (ref: FocusedContextRef) =>
    onContextRefsChange(contextRefs.filter((item) => contextRefKey(item) !== contextRefKey(ref)))

  const onFilesChosen = (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? [])
    // Clearing the input lets the same file be chosen again after it was removed.
    event.target.value = ''
    if (files.length > 0) onAttach?.(files)
  }

  return (
    <Stack spacing={1} sx={{ position: 'relative' }}>
      {isPicking && (
        <MentionPicker
          ref={pickerRef}
          query={mention.query}
          onPick={pick}
          onDismiss={() => setDismissedMentionAt(mention.index)}
        />
      )}
      <ContextRefChips
        contextRefs={contextRefs}
        onRemove={disabled ? undefined : removeContextRef}
        data-testid="assistant-composer-context"
      />
      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }} data-testid="assistant-composer-attachments">
          {attachments.map((item) => (
            <AttachmentChip
              key={item.key}
              filename={item.filename}
              uploading={item.status === 'uploading'}
              failed={item.status === 'failed'}
              title={item.failure ? t(FAILURE_MESSAGES[item.failure]) : undefined}
              onDelete={onRemoveAttachment ? () => onRemoveAttachment(item.key) : undefined}
              deleteLabel={t('removeAttachment', { filename: item.filename })}
            />
          ))}
        </Box>
      )}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        {onAttach && (
          <>
            <input
              ref={fileInput}
              type="file"
              multiple
              hidden
              accept={ACCEPTED_ATTACHMENT_TYPES}
              data-testid="assistant-attachment-input"
              onChange={onFilesChosen}
            />
            <Tooltip title={t('attach')}>
              <span>
                <IconButton aria-label={t('attach')} disabled={disabled} onClick={() => fileInput.current?.click()}>
                  <AttachFile />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
        <TextField
          fullWidth
          multiline
          maxRows={6}
          size="small"
          placeholder={t('placeholder')}
          value={draft}
          disabled={disabled}
          inputRef={inputRef}
          onChange={(event) => changeDraft(event.target.value)}
          onKeyDown={onKeyDown}
          slotProps={{ htmlInput: { 'aria-label': t('placeholder') } }}
        />
        {onStop ? (
          <Tooltip title={t('stop')}>
            <IconButton aria-label={t('stop')} onClick={onStop}>
              <Stop />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title={t('send')}>
            <span>
              <IconButton aria-label={t('send')} color="primary" disabled={!canSend} onClick={send}>
                <ArrowUpward />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>
      <Box>
        <ActModeToggle disabled={disabled} />
      </Box>
    </Stack>
  )
}

/**
 * The `@` being typed: the last one that starts a word, with the text after it. A line break ends it, and an `@`
 * inside a word (an email address) is not one.
 */
function findMention(draft: string): { index: number; query: string } | undefined {
  const index = draft.lastIndexOf('@')
  if (index < 0) return undefined
  const before = draft.at(index - 1)
  if (index > 0 && before !== undefined && !/\s/.test(before)) return undefined
  const query = draft.slice(index + 1)
  if (query.includes('\n')) return undefined
  return { index, query: query.trim() }
}
